import { useTracking } from 'react-tracking';
import { useParams } from '@/hooks/location/useParams';
import { NarrativeCommunity } from '../components/NarrativeCommunity';
import { NarrativeMiniValuator } from '../components/NarrativeMiniValuator';
import { createCompanyCanonicalUrl } from '../components/NarrativeCommunity/utils';
import { cn } from '@simplywallst/ui-core/next';
import { Tab, TabList, useMount } from '@simplywallst/ui-core';
import { useEffect, useState } from 'react';
import { Footer } from '@/components/Footer';
import { SortFieldMap } from '../components/NarrativeCommunity/hooks/useNarrativeCommunitySort';

type TabValue = 'narratives' | 'valuator';

export const NarrativeCommunityPage = () => {
  const { Track, trackEvent } = useTracking({
    type: 'track',
  });
  useMount(() => {
    trackEvent({ subject: 'page', action: 'landed' });
  });
  const [selectedTab, setSelectedTab] = useState<TabValue>('narratives');

  const {
    companyname: companyNameSlug,
    country: market,
    exchange_ticker: exchangeTicker,
    industry: primaryIndustrySlug,
  } = useParams<Record<string, string | undefined>>();

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    const sortHashes = Object.values(SortFieldMap).map((sort) =>
      sort.field.toLowerCase()
    ) as string[];

    // If the hash is some external anchor as opposed to a sort hash, we don't want to scroll to the top
    if (hash && !sortHashes.includes(hash.toLowerCase())) return;

    window.scrollTo(0, 0);
  }, [companyNameSlug, primaryIndustrySlug, market, exchangeTicker]);

  return (
    <Track>
      <div className="relative grid w-full max-w-screen-xl grid-cols-1 grid-rows-[auto_max-content] md:grid-cols-[1fr_minmax(320px,375px)] md:grid-rows-1 md:gap-x3 md:p-x2 lg:p-x3">
        <TabList className="sticky top-x8 z-20 row-start-1 grid-cols-[repeat(2,max-content)] border-0 border-b border-solid border-b-subtle bg-surface-2 px-x2 pt-x1 md:!hidden">
          <Tab<TabValue>
            value="narratives"
            selected={selectedTab === 'narratives'}
            styleType="subnav"
            onClick={() => setSelectedTab('narratives')}
          >
            Narratives
          </Tab>
          <Tab<TabValue>
            value="valuator"
            selected={selectedTab === 'valuator'}
            styleType="subnav"
            onClick={() => setSelectedTab('valuator')}
          >
            Your Valuation
          </Tab>
        </TabList>

        <div
          className={cn(
            'md:row-start-1 md:px-0',
            selectedTab !== 'narratives' && 'hidden md:block'
          )}
        >
          <NarrativeCommunity
            market={market}
            primaryIndustrySlug={primaryIndustrySlug}
            companyNameSlug={companyNameSlug}
            exchangeTicker={exchangeTicker}
          />
        </div>

        <div className="row-start-2 md:row-start-1">
          <div
            className={cn(
              'sticky top-x9 md:grid-rows-[minmax(auto,calc(100dvh-88px))] md:items-start',
              selectedTab !== 'valuator' && 'hidden md:grid'
            )}
          >
            <NarrativeMiniValuator
              companyCanonicalUrl={createCompanyCanonicalUrl({
                primaryIndustrySlug,
                companyNameSlug,
                market,
                exchangeTicker,
              })}
              className="rounded-none md:rounded-x1_5"
            />
          </div>
        </div>

        <div className="col-span-full w-full">
          <Footer />
        </div>
      </div>
    </Track>
  );
};
